/**
 * Portfolio Snapshot Component
 * Displays portfolio details including positions, cash, risk metrics and allocations
 * Extracted from PortfolioComposer for better maintainability
 * TASK-169: UI Translation Content
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCurrency,
  formatCurrencyWithDecimals,
  formatNumber,
  formatPercentage
} from '../../utils/formatters';

interface PortfolioData {
  total_value: number;
  health_score: number;
  positions: Array<{
    symbol?: string;
    isin?: string;
    security_name?: string;
    name?: string;
    currency: string;
    quantity: number;
    market_price: number;
    market_value: number;
    unrealized_pl?: number;
  }>;
  cash?: Array<{
    currency: string;
    amount: number;
    base_amount?: number;
  }>;
  risk_metrics: {
    volatility: number;
    var_1day: number;
    concentration_risk: number;
  };
  allocations: {
    by_asset_class: Record<string, number>;
  };
}

interface PortfolioSnapshotProps {
  portfolio: PortfolioData;
  isSimulated?: boolean;
  currentValue?: number;
}

const PortfolioSnapshot: React.FC<PortfolioSnapshotProps> = ({
  portfolio,
  isSimulated,
  currentValue
}) => {
  const { t } = useTranslation('portfolio');

  const valueDiff = isSimulated && currentValue ? portfolio.total_value - currentValue : 0;
  const valueDiffPercent = currentValue ? (valueDiff / currentValue) * 100 : 0;

  // Calculate total cash across all currencies
  const totalCash = portfolio.cash?.reduce((sum, c) => sum + (c.base_amount || c.amount || 0), 0) || 0;
  const totalSecurities = portfolio.total_value - totalCash;

  const getHealthScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">{t('metrics.total_value')}</p>
          <p className="text-2xl font-bold">{formatCurrency(portfolio.total_value)}</p>
          {isSimulated && valueDiff !== 0 && (
            <p className={`text-sm ${valueDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {valueDiff > 0 ? '+' : ''}{formatCurrency(valueDiff)} ({valueDiffPercent > 0 ? '+' : ''}{formatPercentage(valueDiffPercent)})
            </p>
          )}
          <div className="mt-2 pt-2 border-t space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('sections.securities')}</span>
              <span className="font-medium">{formatCurrency(totalSecurities)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">{t('sections.cash')}</span>
              <span className="font-medium">{formatCurrency(totalCash)}</span>
            </div>
            {portfolio.total_value > 0 && (
              <div className="mt-2">
                <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                  <div 
                    className="bg-blue-600"
                    style={{ width: `${(totalSecurities / portfolio.total_value) * 100}%` }}
                  />
                  <div 
                    className="bg-green-600"
                    style={{ width: `${(totalCash / portfolio.total_value) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-500">
                    {formatPercentage((totalSecurities / portfolio.total_value) * 100)} {t('sections.securities')}
                  </span>
                  <span className="text-gray-500">
                    {formatPercentage((totalCash / portfolio.total_value) * 100)} {t('sections.cash')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-600">{t('metrics.health_score')}</p>
          <p className={`text-2xl font-bold ${getHealthScoreColor(portfolio.health_score)}`}>
            {portfolio.health_score}/100
          </p>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  portfolio.health_score >= 80 ? 'bg-green-600' :
                  portfolio.health_score >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                }`}
                style={{ width: `${portfolio.health_score}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Positions */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3">{t('sections.positions')}</h4>
        {portfolio.positions.length > 0 ? (
          <div className="space-y-2">
            {portfolio.positions.map((position, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">
                        {position.symbol || position.isin || 'Unknown'}
                      </p>
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                        {position.currency}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {position.security_name || position.name || 'Security'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatNumber(position.quantity)} shares @ {formatCurrencyWithDecimals(position.market_price, position.currency, 2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrencyWithDecimals(position.market_value, position.currency, 2)}
                    </p>
                    {position.unrealized_pl !== undefined && position.unrealized_pl !== 0 && (
                      <p className={`text-sm font-medium ${position.unrealized_pl > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {position.unrealized_pl > 0 ? '+' : ''}{formatCurrencyWithDecimals(position.unrealized_pl, position.currency, 2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">{t('empty_states.no_positions')}</p>
        )}
      </div>

      {/* Cash Positions */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3">{t('sections.cash_positions')}</h4>
        {portfolio.cash && portfolio.cash.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {portfolio.cash.map((cash, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{cash.currency}</span>
                  <span className="font-semibold">{formatCurrencyWithDecimals(cash.amount, cash.currency, 2)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">{t('empty_states.no_cash_positions')}</p>
        )}
      </div>

      {/* Risk Metrics */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3">{t('sections.risk_metrics')}</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">{t('metrics.volatility')}</p>
            <p className="font-semibold">{formatPercentage(portfolio.risk_metrics.volatility)}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">{t('metrics.var_1day')}</p>
            <p className="font-semibold">{formatCurrency(portfolio.risk_metrics.var_1day)}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">{t('metrics.concentration')}</p>
            <p className="font-semibold">{formatPercentage(portfolio.risk_metrics.concentration_risk)}</p>
          </div>
        </div>
      </div>

      {/* Allocations */}
      <div>
        <h4 className="font-semibold mb-3">{t('sections.allocations')}</h4>
        <div className="space-y-2">
          {Object.entries(portfolio.allocations.by_asset_class).map(([asset, percent]) => (
            <div key={asset} className="flex justify-between">
              <span className="text-sm">{asset}</span>
              <span className="text-sm font-medium">{formatPercentage(percent)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioSnapshot;